import { useEffect } from 'react'
import { Icon, PATH } from '../icons'
import { fetchDocumentPreview, useAsync } from '../lib/api'
import type { DocumentPreview as DocumentPreviewData } from '../lib/api'

// 點公文卡片後的側邊預覽（#1620，簡易版）。資料每次向伺服器重新取：
// 舊對話裡的卡片不是權限憑證，看不看得到由伺服器當下判定。

/** 檔案大小給人看的字樣。0 或缺值是「沒記錄」，不寫成 0 B。 */
export const formatFileSize = (bytes: number): string => {
  if (!bytes || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="doc-preview-field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  )
}

function PreviewBody({ data }: { data: DocumentPreviewData }) {
  const { document, mainSiteUrl } = data
  return (
    <>
      <h2 className="doc-preview-title">{document.subject || '（未填主旨）'}</h2>
      <dl className="doc-preview-fields">
        <Field label="發文機關" value={document.bureau} />
        <Field label="文號" value={document.issueNo} />
        <Field label="發文日期" value={document.issueDate} />
        <Field label="標籤" value={document.tags.join('、')} />
        <Field label="內容摘要" value={document.contentSummary} />
        <Field label="內容" value={document.content} />
      </dl>
      <h3 className="doc-preview-subtitle">附檔（{document.files.length}）</h3>
      {document.files.length === 0 ? (
        <p className="doc-preview-note">這份文件沒有附檔。</p>
      ) : (
        <ul className="doc-preview-files">
          {document.files.map((file, index) => (
            <li key={`${file.fileName}-${index}`}>
              <span className="doc-preview-file-name">{file.fileName}</span>
              <span className="doc-preview-file-meta">
                {[file.format, formatFileSize(file.sizeBytes)].filter(Boolean).join('・')}
              </span>
            </li>
          ))}
        </ul>
      )}
      {/* 附檔原檔在主前台看；AI 閘道不發附檔網址（#1632 使用者決定） */}
      {mainSiteUrl ? (
        <a className="doc-preview-link" href={mainSiteUrl} target="_blank" rel="noopener noreferrer">
          在主前台開啟這份文件
          <Icon d={PATH.arrowUpRight} size={16} />
        </a>
      ) : null}
    </>
  )
}

interface DocumentPreviewProps {
  documentId: string
  onClose: () => void
}

export function DocumentPreview({ documentId, onClose }: DocumentPreviewProps) {
  const { data, error, loading } = useAsync(() => fetchDocumentPreview(documentId), [documentId])

  // Esc 關掉預覽：側欄蓋住對話時，鍵盤使用者要有路出去
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <aside className="doc-preview" role="dialog" aria-label="文件預覽">
      <header className="doc-preview-head">
        <span className="doc-preview-kicker">文件預覽</span>
        <button type="button" className="doc-preview-close" aria-label="關閉預覽" onClick={onClose}>
          <Icon d={PATH.close} size={18} />
        </button>
      </header>
      <div className="doc-preview-body">
        {loading && <p className="doc-preview-note">正在載入文件…</p>}
        {!loading && error && <p className="doc-preview-error" role="alert">{error}</p>}
        {!loading && data && <PreviewBody data={data} />}
      </div>
    </aside>
  )
}
